import React, { useState, useRef, useEffect, useMemo } from "react";
import { Filter, X, ArrowUpNarrowWide, ArrowDownWideNarrow, ChevronRight, ChevronDown, Search } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * DueFilterPopover — Excel-style compact filter for the Due column.
 *
 * Two filter modes (mutually exclusive — picking one clears the other):
 *   • kind: "operator" — Choose-One dropdown + optional date(s) input
 *       op: "equals" | "before" | "after" | "between" | "today" | "tomorrow" |
 *           "yesterday" | "this-week" | "last-week" | "next-week" |
 *           "this-month" | "last-month" | "next-month" |
 *           "this-quarter" | "last-quarter" | "next-quarter" |
 *           "this-year" | "last-year" | "next-year" | "ytd" | "overdue"
 *       date, date2 — ISO strings for operator types that need them
 *   • kind: "months" — checked YYYY-MM strings (from tree below)
 */

export const OPERATOR_GROUPS = [
  { label: "Range",    ops: [
    { key: "equals",       label: "Equals",        needsDate: 1 },
    { key: "before",       label: "Before",        needsDate: 1 },
    { key: "after",        label: "After",         needsDate: 1 },
    { key: "between",      label: "Between",       needsDate: 2 },
  ]},
  { label: "Day",      ops: [
    { key: "tomorrow",     label: "Tomorrow" },
    { key: "today",        label: "Today" },
    { key: "yesterday",    label: "Yesterday" },
  ]},
  { label: "Week",     ops: [
    { key: "next-week",    label: "Next Week" },
    { key: "this-week",    label: "This Week" },
    { key: "last-week",    label: "Last Week" },
  ]},
  { label: "Month",    ops: [
    { key: "next-month",   label: "Next Month" },
    { key: "this-month",   label: "This Month" },
    { key: "last-month",   label: "Last Month" },
  ]},
  { label: "Quarter",  ops: [
    { key: "next-quarter", label: "Next Quarter" },
    { key: "this-quarter", label: "This Quarter" },
    { key: "last-quarter", label: "Last Quarter" },
  ]},
  { label: "Year",     ops: [
    { key: "next-year",    label: "Next Year" },
    { key: "this-year",    label: "This Year" },
    { key: "last-year",    label: "Last Year" },
  ]},
  { label: "Other",    ops: [
    { key: "ytd",          label: "Year to Date" },
    { key: "overdue",      label: "Overdue" },
  ]},
];
const OPS = OPERATOR_GROUPS.flatMap(g => g.ops);
const OP_BY_KEY = Object.fromEntries(OPS.map(o => [o.key, o]));

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* ── Helpers ──────────────────────────────────────────────────────────── */
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function startOfWeek(d) { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfQuarter(d) { return new Date(d.getFullYear(), Math.floor(d.getMonth()/3)*3, 1); }
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }

function parseDate(str) {
  if (!str) return null;
  const s = String(str).slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : startOfDay(d);
}
function yymKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; }

function rangeForOperator(op, date, date2) {
  const today = startOfDay(new Date());
  const p = parseDate(date);
  const p2 = parseDate(date2);
  switch (op) {
    case "equals":   return p ? { from: p, to: p } : null;
    case "before":   return p ? { from: null, to: addDays(p, -1) } : null;
    case "after":    return p ? { from: addDays(p, 1), to: null } : null;
    case "between":  return (p && p2) ? { from: p, to: p2 } : null;
    case "today":      return { from: today, to: today };
    case "tomorrow":   return { from: addDays(today, 1), to: addDays(today, 1) };
    case "yesterday":  return { from: addDays(today, -1), to: addDays(today, -1) };
    case "this-week":  { const s = startOfWeek(today); return { from: s, to: addDays(s, 6) }; }
    case "next-week":  { const s = addDays(startOfWeek(today), 7); return { from: s, to: addDays(s, 6) }; }
    case "last-week":  { const s = addDays(startOfWeek(today), -7); return { from: s, to: addDays(s, 6) }; }
    case "this-month": { const s = startOfMonth(today); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+1, 1), -1) }; }
    case "next-month": { const s = startOfMonth(new Date(today.getFullYear(), today.getMonth()+1, 1)); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+1, 1), -1) }; }
    case "last-month": { const s = startOfMonth(new Date(today.getFullYear(), today.getMonth()-1, 1)); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+1, 1), -1) }; }
    case "this-quarter": { const s = startOfQuarter(today); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+3, 1), -1) }; }
    case "next-quarter": { const s = startOfQuarter(new Date(today.getFullYear(), today.getMonth()+3, 1)); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+3, 1), -1) }; }
    case "last-quarter": { const s = startOfQuarter(new Date(today.getFullYear(), today.getMonth()-3, 1)); return { from: s, to: addDays(new Date(s.getFullYear(), s.getMonth()+3, 1), -1) }; }
    case "this-year":    return { from: startOfYear(today), to: new Date(today.getFullYear(), 11, 31) };
    case "next-year":    return { from: new Date(today.getFullYear()+1, 0, 1), to: new Date(today.getFullYear()+1, 11, 31) };
    case "last-year":    return { from: new Date(today.getFullYear()-1, 0, 1), to: new Date(today.getFullYear()-1, 11, 31) };
    case "ytd":          return { from: startOfYear(today), to: today };
    case "overdue":      return { from: null, to: addDays(today, -1), special: "overdue" };
    default: return null;
  }
}

/** Returns true if the given date string (ISO-ish) satisfies the filter. */
export function itemMatchesDueFilter(itemDateStr, value) {
  if (!value) return true;
  const d = parseDate(itemDateStr);
  if (!d) return false;
  if (value.kind === "operator") {
    const r = rangeForOperator(value.op, value.date, value.date2);
    if (!r) return true;
    if (r.from && d < r.from) return false;
    if (r.to && d > r.to) return false;
    return true;
  }
  if (value.kind === "months") {
    return (value.months || []).includes(yymKey(d));
  }
  return true;
}

/** Short label for the filter-chip summary. */
export function summarizeDueFilter(value) {
  if (!value) return null;
  if (value.kind === "operator") {
    const meta = OP_BY_KEY[value.op];
    if (!meta) return null;
    if (meta.needsDate === 2) return value.date && value.date2 ? `${value.date} → ${value.date2}` : meta.label;
    if (meta.needsDate === 1) return value.date ? `${meta.label} ${value.date}` : meta.label;
    return meta.label;
  }
  if (value.kind === "months") {
    const n = value.months?.length || 0;
    if (n === 0) return null;
    if (n === 1) {
      const [y, m] = value.months[0].split("-").map(Number);
      return `${MONTH_SHORT[m-1]}'${String(y).slice(2)}`;
    }
    return `${n} months`;
  }
  return null;
}

/* ── Component ───────────────────────────────────────────────────────── */
export default function DueFilterPopover({
  columnLabel,
  value, onChange,
  sort = null, onSortChange = null,
  accentColor = "#f97316",
  testIdPrefix = "due-filter",
  items = [],
  dateField = "due_date",
  dateFieldAlt = "dispatch_due_date",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = !!value;

  // Auto-apply state — selections fire onChange immediately (Excel-style).
  // Draft state remains only for operator + date inputs where the user needs
  // to type a date before the filter becomes meaningful.
  const [draftOp, setDraftOp] = useState("");
  const [draftDate, setDraftDate] = useState("");
  const [draftDate2, setDraftDate2] = useState("");
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [search, setSearch] = useState("");

  // Derive checked months straight from the current value — no draft set needed.
  const checkedMonths = useMemo(() => (
    value?.kind === "months" ? new Set(value.months || []) : new Set()
  ), [value]);

  // Sync operator draft with active value on open
  useEffect(() => {
    if (!open) return;
    if (value?.kind === "operator") {
      setDraftOp(value.op || "");
      setDraftDate(value.date || "");
      setDraftDate2(value.date2 || "");
    } else {
      setDraftOp("");
      setDraftDate("");
      setDraftDate2("");
    }
    setSearch("");
  }, [open, value]);

  // Outside click / ESC handled by Radix Popover automatically

  // Year → month tree from item dates
  const tree = useMemo(() => {
    const map = new Map(); // year -> Set(month)
    const allKeys = [];
    items.forEach(it => {
      const raw = it[dateFieldAlt] || it[dateField];
      const d = parseDate(raw);
      if (!d) return;
      const y = d.getFullYear(), m = d.getMonth();
      if (!map.has(y)) map.set(y, new Set());
      map.get(y).add(m);
      allKeys.push(`${y}-${String(m+1).padStart(2,"0")}`);
    });
    const years = [...map.keys()].sort();
    return { years: years.map(y => ({ year: y, months: [...map.get(y)].sort() })), allKeys: [...new Set(allKeys)] };
  }, [items, dateField, dateFieldAlt]);

  // On first open, expand the current / most-recent year
  useEffect(() => {
    if (!open || expandedYears.size) return;
    if (tree.years.length) {
      const now = new Date().getFullYear();
      const y = tree.years.find(t => t.year === now) ? now : tree.years[tree.years.length - 1].year;
      setExpandedYears(new Set([y]));
    }
  }, [open, tree, expandedYears]);

  const visibleTree = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree.years;
    return tree.years
      .map(y => ({
        year: y.year,
        months: y.months.filter(m => MONTH_LONG[m].toLowerCase().includes(q) || MONTH_SHORT[m].toLowerCase().includes(q) || String(y.year).includes(q)),
      }))
      .filter(y => y.months.length);
  }, [tree, search]);

  const allVisibleKeys = useMemo(() => {
    return visibleTree.flatMap(y => y.months.map(m => `${y.year}-${String(m+1).padStart(2,"0")}`));
  }, [visibleTree]);
  const allSelected = allVisibleKeys.length > 0 && allVisibleKeys.every(k => checkedMonths.has(k));
  const someSelected = allVisibleKeys.some(k => checkedMonths.has(k));

  // Commit a new set of checked months to the parent (or clear the filter).
  const commitMonths = (nextSet) => {
    if (nextSet.size === 0) onChange(null);
    else onChange({ kind: "months", months: [...nextSet].sort() });
  };

  const toggleMonth = (key) => {
    const next = new Set(checkedMonths);
    if (next.has(key)) next.delete(key); else next.add(key);
    // Picking a month clears any operator selection
    if (draftOp) {
      setDraftOp("");
      setDraftDate("");
      setDraftDate2("");
    }
    commitMonths(next);
  };
  const toggleYear = (y) => {
    const keys = tree.years.find(t => t.year === y)?.months.map(m => `${y}-${String(m+1).padStart(2,"0")}`) || [];
    const next = new Set(checkedMonths);
    const allYearChecked = keys.every(k => next.has(k));
    keys.forEach(k => allYearChecked ? next.delete(k) : next.add(k));
    if (draftOp) {
      setDraftOp("");
      setDraftDate("");
      setDraftDate2("");
    }
    commitMonths(next);
  };
  const toggleAllVisible = () => {
    const next = new Set(checkedMonths);
    if (allSelected) allVisibleKeys.forEach(k => next.delete(k));
    else allVisibleKeys.forEach(k => next.add(k));
    if (draftOp) {
      setDraftOp("");
      setDraftDate("");
      setDraftDate2("");
    }
    commitMonths(next);
  };
  const toggleYearExpand = (y) => {
    const next = new Set(expandedYears);
    next.has(y) ? next.delete(y) : next.add(y);
    setExpandedYears(next);
  };

  // Commit operator + dates whenever all required inputs are present.
  const commitOperator = (op, d1, d2) => {
    const meta = OP_BY_KEY[op];
    if (!meta) { onChange(null); return; }
    if (meta.needsDate === 2) {
      if (d1 && d2) onChange({ kind: "operator", op, date: d1, date2: d2 });
    } else if (meta.needsDate === 1) {
      if (d1) onChange({ kind: "operator", op, date: d1 });
    } else {
      onChange({ kind: "operator", op });
    }
  };

  const pickOp = (opKey) => {
    setDraftOp(opKey);
    // Operator + months are mutually exclusive — clear any month selection.
    if (checkedMonths.size > 0) onChange(null);
    const meta = OP_BY_KEY[opKey];
    if (!meta?.needsDate) {
      setDraftDate("");
      setDraftDate2("");
      if (opKey) commitOperator(opKey, "", "");
      else onChange(null);
    } else {
      // Needs a date — wait for the user to pick one before committing.
      if (!opKey) onChange(null);
    }
  };

  const updateDate = (d) => { setDraftDate(d); if (draftOp) commitOperator(draftOp, d, draftDate2); };
  const updateDate2 = (d) => { setDraftDate2(d); if (draftOp) commitOperator(draftOp, draftDate, d); };

  const clearFilter = () => {
    setDraftOp("");
    setDraftDate("");
    setDraftDate2("");
    onChange(null);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{columnLabel}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`relative inline-flex items-center justify-center w-7 h-7 -my-1.5 rounded-full rl-well transition-colors normal-case ${
                    active
                      ? "text-white"
                      : "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))]"
                  }`}
                  style={active ? { background: accentColor } : {}}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  data-testid={`${testIdPrefix}-trigger`}
                >
                  <Filter className="w-3 h-3" />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Filter & sort by due date</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <PopoverContent
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 p-0 w-[288px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-lg overflow-hidden normal-case"
          data-testid={`${testIdPrefix}-panel`}
          ref={ref}
        >
          {/* Sort */}
          {onSortChange && (
            <div className="px-3 pt-2.5 pb-2 relative">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Sort</div>
                {active && (
                  <button
                    type="button"
                    onClick={clearFilter}
                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] normal-case"
                    data-testid={`${testIdPrefix}-clear`}
                  >
                    <X className="w-2.5 h-2.5" /> Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => onSortChange(sort === "asc" ? null : "asc")}
                  className={`flex items-center justify-center gap-1.5 h-7 rounded border text-xs font-semibold transition-colors ${sort === "asc" ? "text-white border-transparent" : "border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"}`}
                  style={sort === "asc" ? { background: accentColor } : {}}
                  data-testid={`${testIdPrefix}-sort-asc`}
                >
                  <ArrowUpNarrowWide className="w-3.5 h-3.5" /> Ascending
                </button>
                <button
                  type="button"
                  onClick={() => onSortChange(sort === "desc" ? null : "desc")}
                  className={`flex items-center justify-center gap-1.5 h-7 rounded border text-xs font-semibold transition-colors ${sort === "desc" ? "text-white border-transparent" : "border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"}`}
                  style={sort === "desc" ? { background: accentColor } : {}}
                  data-testid={`${testIdPrefix}-sort-desc`}
                >
                  <ArrowDownWideNarrow className="w-3.5 h-3.5" /> Descending
                </button>
              </div>
            </div>
          )}
          {/* If there's no sort section, still allow a Clear button at the top-right */}
          {!onSortChange && active && (
            <div className="px-3 pt-2 pb-0 flex justify-end">
              <button
                type="button"
                onClick={clearFilter}
                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] normal-case"
                data-testid={`${testIdPrefix}-clear`}
              >
                <X className="w-2.5 h-2.5" /> Clear
              </button>
            </div>
          )}

          {/* Filter header + operator + date inputs */}
          <div className="px-3 pt-2 pb-2 border-t border-[hsl(var(--border))]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5">Filter</div>
            <div className="flex items-center gap-1.5">
              <select
                value={draftOp}
                onChange={(e) => pickOp(e.target.value)}
                className="flex-1 h-7 px-2 text-[11px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                data-testid={`${testIdPrefix}-op-select`}
              >
                <option value="">Choose One</option>
                {OPERATOR_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.ops.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </optgroup>
                ))}
              </select>
              {OP_BY_KEY[draftOp]?.needsDate ? (
                <input
                  type="date"
                  value={draftDate}
                  onChange={(e) => updateDate(e.target.value)}
                  className="h-7 px-1.5 text-[11px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                  data-testid={`${testIdPrefix}-date-1`}
                />
              ) : (
                <input disabled placeholder="—" className="h-7 px-1.5 text-[11px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 w-[118px] text-[hsl(var(--muted-foreground))] text-center" />
              )}
            </div>
            {OP_BY_KEY[draftOp]?.needsDate === 2 && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-widest shrink-0 w-[92px] text-right">and</span>
                <input
                  type="date"
                  value={draftDate2}
                  onChange={(e) => updateDate2(e.target.value)}
                  className="h-7 px-1.5 text-[11px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))] flex-1"
                  data-testid={`${testIdPrefix}-date-2`}
                />
              </div>
            )}
          </div>

          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[hsl(var(--muted-foreground))]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="w-full h-7 pl-7 pr-2 text-[11px] rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          </div>

          {/* Tree of years → months */}
          <div className="max-h-[220px] overflow-y-auto px-3 pb-3 text-sm">
            {visibleTree.length === 0 ? (
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] py-3 text-center italic">No due dates in this view</div>
            ) : (
              <>
                <TreeCheckbox
                  label="(Select All)"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={toggleAllVisible}
                />
                {visibleTree.map(y => {
                  const yearKeys = y.months.map(m => `${y.year}-${String(m+1).padStart(2,"0")}`);
                  const yearAll = yearKeys.every(k => checkedMonths.has(k));
                  const yearSome = yearKeys.some(k => checkedMonths.has(k));
                  const expanded = expandedYears.has(y.year);
                  return (
                    <div key={y.year}>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => toggleYearExpand(y.year)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] shrink-0 p-0.5" aria-label={expanded ? "Collapse" : "Expand"}>
                          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                        <TreeCheckbox
                          label={String(y.year)}
                          checked={yearAll}
                          indeterminate={!yearAll && yearSome}
                          onChange={() => toggleYear(y.year)}
                          bold
                        />
                      </div>
                      {expanded && (
                        <div className="ml-6">
                          {y.months.map(m => {
                            const key = `${y.year}-${String(m+1).padStart(2,"0")}`;
                            return (
                              <TreeCheckbox
                                key={key}
                                label={MONTH_LONG[m]}
                                checked={checkedMonths.has(key)}
                                onChange={() => toggleMonth(key)}
                                testId={`${testIdPrefix}-month-${key}`}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}

function TreeCheckbox({ label, checked, indeterminate, onChange, bold, testId }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <label className="flex items-center gap-2 py-0.5 text-[11px] cursor-pointer select-none">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-3.5 h-3.5 rounded cursor-pointer accent-[hsl(var(--muted-foreground))]"
        data-testid={testId}
      />
      <span className={bold ? "font-semibold" : ""}>{label}</span>
    </label>
  );
}
